import { useEffect, useMemo, useState } from 'react';
import type { Academy, Lesson, LessonSession } from '@court-sync/shared';
import { ymdTodayForAcademy } from '@/utils/academyCalendarDate';
import { lessonSessionsService } from '@/services/lessonSessions';
import { lessonService } from '@/services/lesson';
import { Input } from '@/components/ui';

const FILTER_SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500';

export interface StudentClassSessionFilterProps {
  academy: Academy;
  sessionId?: string;
  onSessionIdChange: (sessionId: string | undefined) => void;
}

function formatTime(t: string): string {
  return t.slice(0, 5);
}

export function StudentClassSessionFilter({
  academy,
  sessionId,
  onSessionIdChange,
}: StudentClassSessionFilterProps) {
  const minDate = ymdTodayForAcademy(academy);
  const [classDate, setClassDate] = useState(minDate);
  const [sessions, setSessions] = useState<LessonSession[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (classDate < minDate) {
      setClassDate(minDate);
    }
  }, [minDate, classDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [sessionRows, lessonsRes] = await Promise.all([
          lessonSessionsService.listByAcademyAndDateRange(academy.id, classDate, classDate, {
            notifyableOnly: true,
          }),
          lessonService.getLessons({ academyId: academy.id }),
        ]);
        if (cancelled) return;
        setSessions(sessionRows);
        setLessons(lessonsRes.data ?? []);
        if (sessionId && !sessionRows.some((s) => s.id === sessionId)) {
          onSessionIdChange(undefined);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [academy.id, classDate, sessionId, onSessionIdChange]);

  const lessonNameById = useMemo(
    () => new Map(lessons.map((l) => [l.id, l.name])),
    [lessons]
  );

  const selectedLabel = useMemo(() => {
    if (!sessionId) return null;
    const s = sessions.find((row) => row.id === sessionId);
    if (!s) return null;
    const name = lessonNameById.get(s.lessonId) ?? 'Class';
    return `${name} · ${formatTime(s.startTime)}–${formatTime(s.endTime)}`;
  }, [sessionId, sessions, lessonNameById]);

  return (
    <section className="space-y-3 border-t border-border pt-5">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Class on date
        </h4>
        <p className="mt-1 text-xs text-muted-foreground">
          Narrow the list to students enrolled in a specific class occurrence.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="student-filter-class-date"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            Date
          </label>
          <Input
            id="student-filter-class-date"
            type="date"
            min={minDate}
            value={classDate}
            onChange={(e) => {
              onSessionIdChange(undefined);
              setClassDate(e.target.value);
            }}
            className="h-9"
          />
        </div>
        <div>
          <label
            htmlFor="student-filter-class-session"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            Class
          </label>
          <select
            id="student-filter-class-session"
            value={sessionId ?? ''}
            onChange={(e) => onSessionIdChange(e.target.value || undefined)}
            disabled={loading}
            className={FILTER_SELECT_CLASS}
            aria-label="Filter by class session"
          >
            <option value="">
              {loading ? 'Loading classes…' : 'All students'}
            </option>
            {sessions.map((s) => {
              const name = lessonNameById.get(s.lessonId) ?? 'Class';
              return (
                <option key={s.id} value={s.id}>
                  {name} · {formatTime(s.startTime)}–{formatTime(s.endTime)}
                </option>
              );
            })}
          </select>
          {!loading && sessions.length === 0 && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              No upcoming classes on this date.
            </p>
          )}
        </div>
      </div>
      {selectedLabel && (
        <p className="text-xs font-medium text-primary">
          Showing students in: {selectedLabel}
        </p>
      )}
    </section>
  );
}
